import type { CardDef } from '../types.js';
import { createCardElement } from './CardView.js';
import { createShareButton } from './Share.js';

/** Read-only card detail modal. Mirrors `openCardDetail` in
 *  DeckBuilderView, but omits the +/- copy controls and the
 *  "in deck / owned" count — both of which are deck-edit affordances
 *  that don't make sense outside the deck-builder context.
 *
 *  The shared overlay class names (`.card-detail-overlay`,
 *  `.card-detail-modal`, etc.) are reused so the existing CSS in
 *  `css/menu.css` styles us identically — only the controls block is
 *  absent. Esc / backdrop / × all close the modal. */
export function showCardDetail(
  cardId: string,
  catalog: Map<string, CardDef>,
): void {
  const def = catalog.get(cardId);
  if (!def) return;

  // Dedup: a rapid double-tap or repeated keyboard activation can
  // otherwise stack multiple invisible overlays on top of each
  // other (each subsequent one hides the previous). Drop any
  // existing card-detail before opening a new one — switching from
  // card A to card B is the only meaningful "open while already
  // open" flow, and replacing is the right behavior there too. We
  // call the previous overlay's stored `_close` rather than just
  // `.remove()` so its document-level keydown listener is detached
  // along with it (otherwise it leaks until the next Esc).
  document.querySelectorAll('.card-detail-overlay').forEach((o) => {
    const close = (o as HTMLElement & { _close?: () => void })._close;
    if (close) close();
    else o.remove();
  });

  const overlay = document.createElement('div');
  overlay.className = 'card-detail-overlay';

  const flavorBlock = def.flavor
    ? `<div class="card-detail-flavor">"${escapeHtml(def.flavor)}"</div>`
    : '';
  const tagsBlock = def.tags.length > 0
    ? `<div class="card-detail-tags">${
        def.tags.map(t => `<span class="card-detail-tag">${escapeHtml(t)}</span>`).join('')
      }</div>`
    : '';
  const infoBlock = (flavorBlock || tagsBlock)
    ? `<div class="card-detail-info">${flavorBlock}${tagsBlock}</div>`
    : '';

  overlay.innerHTML = `
    <div class="card-detail-modal">
      <div class="card-detail-art">
        <div class="card-detail-card-host"></div>
        <button class="card-detail-close" type="button" aria-label="Close">×</button>
      </div>
      ${infoBlock}
    </div>
  `;
  document.body.appendChild(overlay);

  const shareBtn = createShareButton(cardId, { compact: true });
  shareBtn.classList.add('card-detail-share');
  overlay.querySelector('.card-detail-art')!.appendChild(shareBtn);

  const cardHost = overlay.querySelector('.card-detail-card-host') as HTMLElement;
  cardHost.replaceChildren(
    createCardElement(def, undefined, false, undefined, false, 0, false, true),
  );

  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  // Expose `close` on the overlay element so a follow-up
  // showCardDetail call can tear THIS instance down cleanly (see
  // the dedup loop above). The unknown-first cast is required
  // because HTMLDivElement doesn't structurally include _close.
  (overlay as unknown as { _close: () => void })._close = close;
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('.card-detail-close')?.addEventListener('click', (e) => {
    e.stopPropagation();
    close();
  });
  document.addEventListener('keydown', onKey);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
